// =============================================================================
// aiDiscoveryImport.ts — parse + normalize a pasted AI-discovery payload (v0.2.50)
// =============================================================================
//
// The Inbox's "Import AI Discoveries" form takes JSON that a family's own AI
// produced from the generated discovery prompt (see discoveryPrompt.ts). LLM
// output is close to the schema but rarely perfect: it may be wrapped in a
// ```json fence, preceded by a sentence, missing an optional field, or use
// the wrong type for one. Rather than reject the whole paste, this module:
//
//   1. strips a markdown fence / leading prose so JSON.parse can run,
//   2. validates the envelope ({ ai_discovery_run: { items: [...] } }),
//   3. normalizes every item to the exact AiDiscoveryImportItem shape —
//      coercing types, defaulting optional fields, and clamping enums —
//   4. drops only items that are unusable (no name or sponsor), reporting
//      how many were dropped so the user knows.
//
// Pure functions, no Firebase — exercised by scripts/test-discovery-prompt.ts.
// =============================================================================

import type { AiDiscoveryImportItem } from '@scholarship-app/types';

export interface ParsedAiDiscoveryRun {
  /** ISO date (YYYY-MM-DD). Falls back to today when the payload omits it. */
  runDate: string;
  applicantName: string;
  items: AiDiscoveryImportItem[];
  /** Items dropped because they lacked a usable name or sponsor. */
  malformed: number;
}

/**
 * Pull the JSON object out of pasted text. Handles a ```json fence, a bare
 * ``` fence, and prose before/after the object by slicing from the first "{"
 * to the last "}". Returns the original (trimmed) text when no brace pair is
 * found so JSON.parse can produce its own error.
 */
export function extractJsonObject(text: string): string {
  let s = text.trim();
  // Strip a leading ``` / ```json fence and a trailing ``` fence.
  s = s.replace(/^```[a-zA-Z]*\s*/, '').replace(/\s*```$/, '').trim();
  const first = s.indexOf('{');
  const last = s.lastIndexOf('}');
  if (first !== -1 && last > first) {
    s = s.slice(first, last + 1);
  }
  return s;
}

const MATCH_STRENGTHS = new Set(['high', 'medium', 'low']);
const COMPETITIVENESS = new Set(['low', 'medium', 'high']);
const EFFORTS = new Set(['S', 'M', 'L']);

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function strOrNull(v: unknown): string | null {
  const s = str(v);
  return s ? s : null;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const l = v.trim().toLowerCase();
    if (l === 'true' || l === 'yes') return true;
    if (l === 'false' || l === 'no') return false;
  }
  return fallback;
}

function boolOrNull(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const l = v.trim().toLowerCase();
    if (l === 'true' || l === 'yes') return true;
    if (l === 'false' || l === 'no') return false;
  }
  return null;
}

/**
 * Accepts 5000, "5000", "$5,000", "5,000.00", and ranges like
 * "$1,000 - $5,000" / "1000–5000" (returns [lo, hi]). Zero or negative
 * amounts are treated as unknown (null) — a $0 award is never real data and
 * a 0 would render as a stray "0" on the card. Anything else → null.
 */
function amountRange(v: unknown): [number | null, number | null] {
  const positive = (n: number): number | null => (Number.isFinite(n) && n > 0 ? n : null);
  if (typeof v === 'number') return [positive(v), positive(v)];
  if (typeof v !== 'string') return [null, null];
  const cleaned = v.replace(/[$,\s]/g, '').replace(/[–—]/g, '-');
  if (!cleaned) return [null, null];
  const range = cleaned.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
  if (range) return [positive(Number(range[1])), positive(Number(range[2]))];
  const n = positive(Number(cleaned));
  return [n, n];
}

function tags(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((t): t is string => typeof t === 'string')
    .map((t) => t.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''))
    .filter((t) => t.length > 0);
}

/**
 * Coerce one raw item into the exact AiDiscoveryImportItem shape. Returns
 * null when the item has no usable name or sponsor — the fingerprint (and
 * the whole inbox card) is built from those two, so they're mandatory.
 */
export function normalizeAiDiscoveryItem(raw: unknown): AiDiscoveryImportItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const name = str(r.name);
  const sponsor = str(r.sponsor);
  if (!name || !sponsor) return null;

  const matchStrength = str(r.match_strength).toLowerCase();
  const competitiveness = str(r.competitiveness).toLowerCase();
  const effort = str(r.effort).toUpperCase();
  // Each field may itself be a range string ("$1,000 - $5,000"): take the
  // low end for amount_min and the high end for amount_max.
  const [minLo, minHi] = amountRange(r.amount_min);
  const [maxLo, maxHi] = amountRange(r.amount_max);
  const amountMin = minLo ?? maxLo;
  const amountMax = maxHi ?? minHi;

  return {
    name,
    sponsor,
    url: strOrNull(r.url),
    url_verified: bool(r.url_verified, false),
    // A single-amount award often arrives as amount_max only (or min only);
    // mirror it so the card's "$min–$max" line renders.
    amount_min: amountMin ?? amountMax,
    amount_max: amountMax ?? amountMin,
    amount_notes: str(r.amount_notes),
    deadline_approx: strOrNull(r.deadline_approx),
    deadline_rolling: bool(r.deadline_rolling, false),
    match_strength: (MATCH_STRENGTHS.has(matchStrength) ? matchStrength : 'medium') as AiDiscoveryImportItem['match_strength'],
    why_match: str(r.why_match),
    competitiveness: (COMPETITIVENESS.has(competitiveness) ? competitiveness : null) as AiDiscoveryImportItem['competitiveness'],
    effort: (EFFORTS.has(effort) ? effort : 'M') as AiDiscoveryImportItem['effort'],
    tags: tags(r.tags),
    requirements_summary: str(r.requirements_summary),
    need_based: bool(r.need_based, false),
    homeschool_friendly: boolOrNull(r.homeschool_friendly),
    notes: strOrNull(r.notes),
  };
}

/**
 * Parse pasted text into a normalized run. Throws with a user-facing message
 * when the text isn't JSON or lacks the ai_discovery_run.items envelope.
 */
export function parseAiDiscoveryPayload(text: string, today: Date = new Date()): ParsedAiDiscoveryRun {
  let parsed: unknown;
  try {
    parsed = JSON.parse(extractJsonObject(text));
  } catch {
    throw new Error(
      "Couldn't read that as JSON. Paste the AI's entire output — it should start with { and end with }."
    );
  }
  const run = (parsed as { ai_discovery_run?: unknown })?.ai_discovery_run as Record<string, unknown> | undefined;
  if (!run || typeof run !== 'object' || !Array.isArray(run.items)) {
    throw new Error('JSON missing ai_discovery_run.items array — make sure the AI used the output format from the prompt.');
  }

  const items: AiDiscoveryImportItem[] = [];
  let malformed = 0;
  for (const raw of run.items) {
    const item = normalizeAiDiscoveryItem(raw);
    if (item) items.push(item);
    else malformed++;
  }

  const runDateRaw = str(run.run_date);
  const runDate = /^\d{4}-\d{2}-\d{2}$/.test(runDateRaw) ? runDateRaw : today.toISOString().slice(0, 10);

  return {
    runDate,
    applicantName: str(run.applicant_name),
    items,
    malformed,
  };
}
